import React, { useState, useEffect, useCallback } from "react";
import QRCode from "react-qr-code";
import Barcode from "react-barcode";
import axios from "../../../axios/axios";
import { useMarketplaceInfo } from "../../../hooks/useMarketplaceInfo";
import { useNotification } from "../../../contexts/NotificationContext";
import { UserLoyaltyCardResponse } from "../../../types/loyaltyCard";
import styles from "./LoyaltyCardInfo.module.scss";

// Athos Pharm whitelabel: scanners require a 1D Code128 barcode instead of the default QR.
const BARCODE_BRANCH_ID = 142;

// Simple cache to prevent duplicate API calls for same user
const loyaltyCardCache = new Map<string, UserLoyaltyCardResponse[]>();

interface LoyaltyCardInfoProps {
  userId: number;
}

/**
 * Copy card number to clipboard with user notification
 */
const copyToClipboard = async (
  cardNumber: string,
  showNotification: (type: "success" | "error", message: string) => void,
): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(cardNumber);
    showNotification(
      "success",
      `Broj kartice ${cardNumber} kopiran u clipboard`,
    );
    return true;
  } catch (error) {
    console.error("Failed to copy card number:", error);
    showNotification("error", "Greška pri kopiranju broja kartice");
    return false;
  }
};

/**
 * Generic Copyable Wrapper Component
 * Makes any content clickable and copyable with full accessibility support
 */
const Copyable: React.FC<{
  copyValue: string;
  showNotification: (type: "success" | "error", message: string) => void;
  className?: string;
  title?: string;
  ariaLabel?: string;
  children: React.ReactNode;
}> = ({
  copyValue,
  showNotification,
  className,
  title,
  ariaLabel,
  children,
}) => {
  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      await copyToClipboard(copyValue, showNotification);
    },
    [copyValue, showNotification],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        handleCopy(e as any);
      }
    },
    [handleCopy],
  );

  return (
    <div
      className={className}
      onClick={handleCopy}
      onKeyDown={handleKeyDown}
      title={title}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
    >
      {children}
    </div>
  );
};

/**
 * Loyalty Card Code Component
 * Renders Code128 barcode for the Athos Pharm whitelabel, QR code otherwise.
 */
const LoyaltyCardCode: React.FC<{
  cardNumber: string;
  branchMarketplaceId: number | undefined;
  showNotification: (type: "success" | "error", message: string) => void;
}> = ({ cardNumber, branchMarketplaceId, showNotification }) => {
  // Handle empty or invalid card numbers
  if (!cardNumber || cardNumber.trim() === "") {
    return (
      <div className={styles.qrError}>
        <span>QR nedostupan</span>
      </div>
    );
  }

  const useBarcode = branchMarketplaceId === BARCODE_BRANCH_ID;
  const trimmed = cardNumber.trim();

  try {
    return (
      <Copyable
        copyValue={trimmed}
        showNotification={showNotification}
        className={`${styles.qrCodeWrapper} ${useBarcode ? styles.barcodeWrapper : ""}`}
        title="Kliknite da kopirate broj kartice"
        ariaLabel={`${useBarcode ? "Barkod" : "QR kod"} za kartu ${cardNumber}, kliknite da kopirate`}
      >
        {useBarcode ? (
          <Barcode
            value={trimmed}
            format="CODE128"
            height={50}
            width={2}
            fontSize={12}
            margin={6}
          />
        ) : (
          <QRCode
            value={trimmed}
            size={60}
            level="M"
            className={styles.qrCode}
          />
        )}
      </Copyable>
    );
  } catch (error) {
    console.error("Loyalty card code generation failed:", error);
    return (
      <div className={styles.qrError}>
        <span>QR nedostupan</span>
      </div>
    );
  }
};

/**
 * Individual Loyalty Card Component
 */
const LoyaltyCardItem: React.FC<{
  card: UserLoyaltyCardResponse;
  branchMarketplaceId: number | undefined;
  showNotification: (type: "success" | "error", message: string) => void;
}> = ({ card, branchMarketplaceId, showNotification }) => {
  const useBarcode = branchMarketplaceId === BARCODE_BRANCH_ID;
  return (
    <div key={card.id} className={styles.loyaltyCard}>
      <div
        className={`${styles.cardContent} ${useBarcode ? styles.barcodeMode : ""}`}
      >
        <div className={styles.cardInfo}>
          <div className={styles.infoItem}>
            <div className={styles.label}>Broj kartice:</div>
            <Copyable
              copyValue={card.cardNumber}
              showNotification={showNotification}
              className={styles.copyableValue}
              title="Kliknite da kopirate broj kartice"
              ariaLabel={`Broj kartice ${card.cardNumber}, kliknite da kopirate`}
            >
              <strong>{card.cardNumber}</strong>
            </Copyable>
          </div>
          {card.created && (
            <div className={styles.infoItem}>
              <div className={styles.label}>Član od:</div>
              <strong className={styles.value}>
                {new Date(card.created).toLocaleDateString("sr-RS")}
              </strong>
            </div>
          )}
        </div>
        <LoyaltyCardCode
          cardNumber={card.cardNumber}
          branchMarketplaceId={branchMarketplaceId}
          showNotification={showNotification}
        />
      </div>
    </div>
  );
};

/**
 * Main Loyalty Card Info Component
 */
const LoyaltyCardInfo: React.FC<LoyaltyCardInfoProps> = ({ userId }) => {
  const { branchMarketplaceId, isReady, isAdmin } = useMarketplaceInfo();
  const { showNotification } = useNotification();

  const [loyaltyCards, setLoyaltyCards] = useState<UserLoyaltyCardResponse[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLoyaltyCards = useCallback(
    async (userId: number, branchId: number) => {
      const cacheKey = `${userId}-${branchId}`;

      // Check cache first
      if (loyaltyCardCache.has(cacheKey)) {
        setLoyaltyCards(loyaltyCardCache.get(cacheKey)!);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `/user_loyalty_card/user_id/${userId}/branch_marketplace_id/${branchId}`,
        );

        const cards = response.data || [];
        loyaltyCardCache.set(cacheKey, cards);
        setLoyaltyCards(cards);
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.message || "Failed to fetch loyalty cards";
        setError(errorMessage);
        console.error("Error fetching loyalty cards:", err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (isAdmin || !isReady || !branchMarketplaceId || !userId) {
      return;
    }

    fetchLoyaltyCards(userId, branchMarketplaceId);
  }, [userId, branchMarketplaceId, isReady, isAdmin, fetchLoyaltyCards]);

  // Early exits
  if (isAdmin) return null;
  if (!isReady || !branchMarketplaceId) return null;

  // Loading state
  if (loading) {
    return (
      <div className={styles.loyaltyCardContainer}>
        <div className={styles.loading}>
          Učitavanje informacija o loyalty kartici...
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.loyaltyCardContainer}>
        <div className={styles.header}>Loyalty Program</div>
        <div className={styles.error}>
          Nije moguće učitati informacije o loyalty kartici
          {process.env.NODE_ENV === "development" && (
            <div className={styles.errorDetails}>Error: {error}</div>
          )}
        </div>
      </div>
    );
  }

  // No data
  if (!loyaltyCards.length) return null;

  // Render cards (rest of component stays the same)
  return (
    <div className={styles.loyaltyCardContainer}>
      <div className={styles.header}>Loyalty Program Član</div>
      {loyaltyCards.map((card) => (
        <LoyaltyCardItem
          key={card.id}
          card={card}
          branchMarketplaceId={branchMarketplaceId}
          showNotification={showNotification}
        />
      ))}
    </div>
  );
};

export default LoyaltyCardInfo;
